<template>
  <div class="so-quote">
    <KTCodePreview v-bind:title="'Báo giá phiếu dịch vụ'">
      <template v-slot:toolbar>
        <span v-if="order" class="so-quote-status">
          <b-badge :variant="statusVariant">{{ statusLabel }}</b-badge>
          <span class="so-quote-no">{{ order.orderNo }}</span>
        </span>
      </template>

      <template v-slot:preview>
        <b-overlay :show="loading" rounded>
          <!-- ============ Section 1: Thông tin chung ============ -->
          <div class="so-card">
            <h6 class="so-card-title">Thông tin chung</h6>
            <b-row v-if="order">
              <b-col md="4" class="so-ro">
                <span>Số phiếu:</span> <b>{{ order.orderNo }}</b>
              </b-col>
              <b-col md="4" class="so-ro">
                <span>Ngày tạo:</span> {{ formatDate(order.createdAt) }}
              </b-col>
              <b-col md="4" class="so-ro">
                <span>Loại phiếu:</span> {{ serviceTypeLabel }}
              </b-col>
              <b-col md="4" class="so-ro">
                <span>Khách hàng:</span> {{ order.customerName }}
              </b-col>
              <b-col md="4" class="so-ro">
                <span>Điện thoại:</span> {{ order.customerPhone }}
              </b-col>
              <b-col md="4" class="so-ro">
                <span>Biển số:</span> {{ order.licensePlate }}
              </b-col>
              <b-col md="4" class="so-ro">
                <span>Kiểu xe:</span> {{ order.vehicleModel }}
              </b-col>

              <b-col md="4">
                <label class="so-label">Địa chỉ KH</label>
                <b-form-input
                  v-model="form.customerAddress"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">Email KH</label>
                <b-form-input
                  v-model="form.customerEmail"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">Số khung</label>
                <b-form-input
                  v-model="form.chassisNumber"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">Người mang xe</label>
                <b-form-input
                  v-model="form.carBringer"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">SĐT người mang xe</label>
                <b-form-input
                  v-model="form.carBringerPhone"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">Số km (odometer)</label>
                <b-form-input
                  v-model.number="form.odometer"
                  type="number"
                  min="0"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">Giám định viên</label>
                <b-form-input
                  v-model="form.surveyorName"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">Dự kiến giao xe</label>
                <b-form-input
                  v-model="form.estimatedDeliveryTime"
                  type="datetime-local"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="4">
                <label class="so-label">Mục đích sử dụng</label>
                <b-form-input
                  v-model="form.usagePurpose"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
              <b-col md="12">
                <label class="so-label">Yêu cầu khách hàng</label>
                <b-form-textarea
                  v-model="form.customerRequest"
                  rows="2"
                  size="sm"
                  :disabled="!canEdit"
                />
              </b-col>
            </b-row>
          </div>

          <!-- ============ Section 2: Danh sách DV & phụ tùng ============ -->
          <div class="so-card mt-4">
            <div class="d-flex justify-content-between align-items-center mb-2">
              <h6 class="so-card-title mb-0">
                Danh sách dịch vụ &amp; phụ tùng
              </h6>
              <b-button
                v-if="canEdit"
                size="sm"
                variant="outline-primary"
                @click="addItem"
              >
                <i class="fas fa-plus"></i> Thêm dòng
              </b-button>
            </div>

            <div class="table-responsive">
              <table class="table table-bordered so-item-table">
                <thead>
                  <tr>
                    <th style="width: 40px">STT</th>
                    <th style="width: 150px">Nhóm</th>
                    <th class="so-col-name">Tên SP / Dịch vụ</th>
                    <th style="width: 80px">ĐVT</th>
                    <th class="text-right" style="width: 160px">Đơn giá</th>
                    <th class="text-center" style="width: 80px">SL</th>
                    <th class="text-center" style="width: 150px">Giảm giá</th>
                    <th class="text-right" style="width: 130px">Thành tiền</th>
                    <th class="text-right" style="width: 110px">CK phân bổ</th>
                    <th class="text-center" style="width: 90px">VAT</th>
                    <th class="text-right" style="width: 120px">Tiền VAT</th>
                    <th style="width: 130px">KTV</th>
                    <th style="width: 40px"></th>
                  </tr>
                </thead>
                <tbody>
                  <tr v-for="(it, idx) in items" :key="idx">
                    <td class="text-center align-middle">{{ idx + 1 }}</td>
                    <td>
                      <treeselect
                        :value="it.productGroupId"
                        :options="categoryTree"
                        :normalizer="normalizerCate"
                        :multiple="false"
                        :clearable="true"
                        :show-count="true"
                        :match-keys="['id', 'name']"
                        :disabled="!canEdit"
                        :append-to-body="true"
                        :z-index="2000"
                        open-direction="bottom"
                        placeholder="-- Nhóm --"
                        class="so-cate-tree"
                        @input="(val) => onGroupChange(it, val)"
                      />
                    </td>
                    <td class="so-col-name">
                      <multiselect
                        v-model="it.productSelected"
                        :options="productOptions"
                        :loading="productLoading"
                        :internal-search="false"
                        :clear-on-select="true"
                        :close-on-select="true"
                        :show-labels="false"
                        :disabled="!canEdit"
                        track-by="id"
                        label="label"
                        placeholder="Tìm SP / dịch vụ"
                        open-direction="bottom"
                        @search-change="onProductSearch"
                        @select="(opt) => onProductSelected(it, opt)"
                        @remove="() => onProductRemoved(it)"
                      >
                        <template slot="singleLabel" slot-scope="{ option }">
                          <span class="so-ms-name">{{ option.name }}</span>
                        </template>
                        <template slot="option" slot-scope="{ option }">
                          <div class="so-ms-option">
                            <span class="so-ms-name">{{ option.name }}</span>
                            <span v-if="option.code" class="so-ms-code">{{
                              option.code
                            }}</span>
                          </div>
                        </template>
                        <span slot="noResult">Không có kết quả</span>
                        <span slot="noOptions">Nhập để tìm SP / dịch vụ</span>
                      </multiselect>
                    </td>
                    <td>
                      <b-form-input
                        v-model="it.unit"
                        size="sm"
                        :disabled="!canEdit"
                      />
                    </td>
                    <td>
                      <BInputNumber
                        v-model="it.unitPrice"
                        :disabled="!canEdit"
                        inputClass="w-100 text-right"
                        placeholder="0"
                      />
                    </td>
                    <td>
                      <b-form-input
                        v-model.number="it.quantity"
                        type="number"
                        min="0"
                        step="any"
                        size="sm"
                        class="text-center"
                        :disabled="!canEdit"
                      />
                    </td>
                    <td>
                      <div class="so-disc d-flex align-items-stretch">
                        <div class="so-disc-input">
                          <BInputNumber
                            v-model="it.discountAmount"
                            :allow-decimal="
                              it.discountType === DISCOUNT_TYPE.PERCENT
                            "
                            :disabled="!canEdit"
                            inputClass="w-100 text-right"
                            placeholder="0"
                          />
                        </div>
                        <b-button
                          size="sm"
                          variant="secondary"
                          class="so-disc-toggle"
                          :disabled="!canEdit"
                          :title="discTitle(it.discountType)"
                          @click="
                            toggleDiscType(it, 'discountType', 'discountAmount')
                          "
                        >
                          <i
                            :class="
                              it.discountType === DISCOUNT_TYPE.PERCENT
                                ? 'fa fa-percent'
                                : 'fas fa-dollar-sign'
                            "
                          ></i>
                        </b-button>
                      </div>
                    </td>
                    <td class="text-right align-middle">
                      {{ formatPrice(calc.lines[idx].subTotal) }}
                    </td>
                    <td class="text-right align-middle text-warning">
                      {{ formatPrice(calc.lines[idx].discountAllocation) }}
                    </td>
                    <td class="text-center align-middle">
                      <!-- VAT theo product (BE ghi đè) — read-only, SA không sửa. -->
                      <span class="so-vat-ro">{{ it.vatType || '—' }}</span>
                    </td>
                    <td class="text-right align-middle">
                      {{ formatPrice(calc.lines[idx].vatAmount) }}
                    </td>
                    <td>
                      <b-form-input
                        v-model="it.technicianName"
                        size="sm"
                        :disabled="!canEdit"
                      />
                    </td>
                    <td class="text-center align-middle">
                      <b-button
                        v-if="canEdit"
                        size="sm"
                        variant="link"
                        class="text-danger p-0"
                        @click="removeItem(idx)"
                      >
                        <i class="fas fa-trash"></i>
                      </b-button>
                    </td>
                  </tr>
                  <tr v-if="!items.length">
                    <td colspan="13" class="text-center text-muted py-3">
                      Chưa có dòng nào. Bấm "Thêm dòng" để thêm dịch vụ/phụ
                      tùng.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </div>

          <!-- ============ Section 3: Thông tin thanh toán ============ -->
          <div class="so-card mt-4">
            <h6 class="so-card-title">Thông tin thanh toán</h6>
            <b-row>
              <b-col md="6">
                <label class="so-label">Chiết khấu phiếu</label>
                <div class="so-disc d-flex align-items-stretch">
                  <div class="so-disc-input">
                    <BInputNumber
                      v-model="form.discountAmount"
                      :allow-decimal="
                        form.discountType === DISCOUNT_TYPE.PERCENT
                      "
                      :disabled="!canEdit"
                      inputClass="w-100 text-right"
                      placeholder="0"
                    />
                  </div>
                  <b-button
                    size="sm"
                    variant="secondary"
                    class="so-disc-toggle"
                    :disabled="!canEdit"
                    :title="discTitle(form.discountType)"
                    @click="
                      toggleDiscType(form, 'discountType', 'discountAmount')
                    "
                  >
                    <i
                      :class="
                        form.discountType === DISCOUNT_TYPE.PERCENT
                          ? 'fa fa-percent'
                          : 'fas fa-dollar-sign'
                      "
                    ></i>
                  </b-button>
                </div>
              </b-col>
              <b-col md="6">
                <label class="so-label">Coupon</label>
                <div class="so-disc d-flex align-items-stretch">
                  <div class="so-disc-input">
                    <BInputNumber
                      v-model="form.discountCouponAmount"
                      :allow-decimal="
                        form.discountCouponType === DISCOUNT_TYPE.PERCENT
                      "
                      :disabled="!canEdit"
                      inputClass="w-100 text-right"
                      placeholder="0"
                    />
                  </div>
                  <b-button
                    size="sm"
                    variant="secondary"
                    class="so-disc-toggle"
                    :disabled="!canEdit"
                    :title="discTitle(form.discountCouponType)"
                    @click="
                      toggleDiscType(
                        form,
                        'discountCouponType',
                        'discountCouponAmount',
                      )
                    "
                  >
                    <i
                      :class="
                        form.discountCouponType === DISCOUNT_TYPE.PERCENT
                          ? 'fa fa-percent'
                          : 'fas fa-dollar-sign'
                      "
                    ></i>
                  </b-button>
                </div>
              </b-col>
            </b-row>

            <hr />

            <b-row>
              <b-col md="6">
                <div class="so-total-row">
                  <span>Tổng trước chiết khấu</span>
                  <b>{{ formatPrice(calc.totals.totalBeforeDiscount) }}</b>
                </div>
                <div class="so-total-row text-warning">
                  <span>Tổng chiết khấu</span>
                  <b>- {{ formatPrice(calc.totals.totalDiscount) }}</b>
                </div>
                <div class="so-total-row">
                  <span>Tiền hàng (trước VAT)</span>
                  <b>{{ formatPrice(calc.totals.totalBeforeVAT) }}</b>
                </div>
                <div class="so-total-row">
                  <span>VAT (bóc tách)</span>
                  <b>{{ formatPrice(calc.totals.totalVAT) }}</b>
                </div>
                <div class="so-total-row so-total-final">
                  <span>TỔNG PHẢI THU (đã gồm VAT)</span>
                  <b>{{ formatPrice(calc.totals.totalAfterVAT) }}</b>
                </div>
                <div class="so-total-note">
                  Giá đã bao gồm VAT — VAT chỉ bóc tách để kê khai, không cộng thêm.
                </div>
              </b-col>
              <b-col md="6">
                <label class="so-label">Bảo hiểm thanh toán</label>
                <BInputNumber
                  v-model="form.insurancePayAmount"
                  :disabled="!canEdit || !order || !order.insuranceCompanyId"
                  inputClass="w-100 text-right"
                  placeholder="0"
                />
                <label class="so-label mt-2">Khách hàng thanh toán</label>
                <b-form-input
                  :value="formatPrice(customerPayAmount)"
                  size="sm"
                  class="text-right"
                  readonly
                />
                <b-alert
                  :show="payWarning"
                  variant="danger"
                  class="mt-2 py-2 px-3"
                >
                  Bảo hiểm thanh toán vượt tổng sau VAT — khách hàng phải trả
                  âm. Không thể duyệt.
                </b-alert>
              </b-col>
            </b-row>
          </div>

          <!-- ============ Actions ============ -->
          <!-- Màn này CHỈ sửa nội dung báo giá. Mọi CTA chuyển trạng thái
               (Duyệt báo giá, Duyệt lệnh SC, Huỷ) nằm ở màn Chi tiết (workflow SO-003). -->
          <div v-if="canEdit" class="mt-4 text-right">
            <b-button
              variant="secondary"
              class="mr-2"
              :disabled="saving"
              @click="goDetail"
            >
              Về chi tiết
            </b-button>
            <b-button variant="primary" :disabled="saving" @click="onUpdate">
              <b-spinner v-if="saving" small /> Cập nhật
            </b-button>
          </div>
        </b-overlay>
      </template>
    </KTCodePreview>
  </div>
</template>

<script>
import moment from 'moment';
import debounce from 'debounce';
import { get } from 'lodash';
import Multiselect from 'vue-multiselect';
import 'vue-multiselect/dist/vue-multiselect.min.css';
import Treeselect from '@riophae/vue-treeselect';
import '@riophae/vue-treeselect/dist/vue-treeselect.css';
import KTCodePreview from '@/view/content/CodePreview.vue';
import { SET_BREADCRUMB } from '@/core/services/store/modules/breadcrumbs.module';
import productService from '@/core/services/product';
import CategoryService from '@/core/services/category.service';
import ServiceOrderService from '@/core/services/serviceOrder.service';
import {
  SO_STATUS,
  SO_STATUS_LABEL,
  SO_STATUS_VARIANT,
  SERVICE_TYPE_LABEL,
  DISCOUNT_TYPE,
  vatTypeToRate,
} from '@/core/constants/serviceOrder';
import BInputNumber from '@/view/base/bootstrap/BInputNumber.vue';
import { convertPrice, makeToastSuccess, makeToastFaile } from '@/utils/common';
import { checkPermissions } from '@/utils/saveDataToLocal';
import { computeQuote, round2 } from '@/utils/serviceOrderCalc';

const num = (v) => Number(v) || 0;

const emptyItem = () => ({
  productSelected: null,
  productId: null,
  productName: '',
  productGroupId: null,
  productGroupName: null,
  technicianName: '',
  unit: '',
  unitPrice: 0,
  quantity: 1,
  discountType: DISCOUNT_TYPE.AMOUNT,
  discountAmount: 0,
  vatType: '0%',
  vatRate: 0,
  notes: '',
});

export default {
  name: 'QuotationEdit',
  components: { KTCodePreview, Multiselect, Treeselect, BInputNumber },
  props: {
    id: { type: String, default: '' },
  },
  data() {
    return {
      loading: false,
      saving: false,
      DISCOUNT_TYPE,
      order: null,
      items: [],
      productOptions: [],
      productLoading: false,
      // Cây danh mục (nested) cho treeselect + map id→tên để resolve productGroupName.
      categoryTree: [],
      categoryNameById: {},
      form: {
        customerAddress: '',
        customerEmail: '',
        chassisNumber: '',
        carBringer: '',
        carBringerPhone: '',
        odometer: null,
        surveyorName: '',
        estimatedDeliveryTime: '',
        usagePurpose: '',
        customerRequest: '',
        discountType: DISCOUNT_TYPE.AMOUNT,
        discountAmount: 0,
        discountCouponType: DISCOUNT_TYPE.AMOUNT,
        discountCouponAmount: 0,
        insurancePayAmount: 0,
      },
    };
  },
  computed: {
    canEdit() {
      if (!this.order) return false;
      const editable =
        this.order.status === SO_STATUS.QUOTING ||
        this.order.status === SO_STATUS.QUOTED;
      return editable && checkPermissions(['SERVICE_ORDER_QUOTE_EDIT']);
    },
    statusLabel() {
      return this.order ? SO_STATUS_LABEL[this.order.status] : '';
    },
    statusVariant() {
      return this.order ? SO_STATUS_VARIANT[this.order.status] : 'secondary';
    },
    serviceTypeLabel() {
      return this.order ? SERVICE_TYPE_LABEL[this.order.serviceType] : '';
    },
    // Tính tiền realtime qua engine dùng chung (mirror BE so-calc — SRS C3).
    calc() {
      const r = computeQuote(
        this.items.map((it) => ({
          unitPrice: num(it.unitPrice),
          quantity: it.quantity,
          discountType: it.discountType,
          discountAmount: it.discountAmount,
          vatRate: it.vatRate,
        })),
        {
          discountType: this.form.discountType,
          discountAmount: this.form.discountAmount,
          discountCouponType: this.form.discountCouponType,
          discountCouponAmount: this.form.discountCouponAmount,
        },
      );
      return { lines: r.items, totals: r.totals };
    },
    customerPayAmount() {
      return round2(
        this.calc.totals.totalAfterVAT - num(this.form.insurancePayAmount),
      );
    },
    // CustomerPay = afterVAT − BH (luôn cân bằng), nên chỉ cần cảnh báo khi BH > afterVAT
    // → khách phải trả ÂM. Đây cũng là điều kiện BE chặn duyệt.
    payWarning() {
      return this.customerPayAmount < 0;
    },
  },
  created() {
    this.onProductSearch = debounce(this.runProductSearch, 400);
    this.loadCategories();
    this.loadDetail();
  },
  mounted() {
    this.$store.dispatch(SET_BREADCRUMB, [
      { title: 'Phiếu dịch vụ', route: '/service-orders' },
      { title: 'Báo giá' },
    ]);
  },
  methods: {
    formatDate(d) {
      return d ? moment(d).format('DD/MM/YYYY HH:mm') : '—';
    },
    formatPrice(v) {
      return convertPrice(Number(v) || 0) + ' đ';
    },
    async loadDetail() {
      this.loading = true;
      try {
        const res = await ServiceOrderService.getById(this.id);
        const data = (res.data && res.data.data) || {};
        this.order = data;
        this.fillForm(data);
        this.items = (data.items || []).map((it) => this.toRow(it));
      } catch (e) {
        makeToastFaile('Không tải được phiếu dịch vụ');
      } finally {
        this.loading = false;
      }
    },
    fillForm(d) {
      this.form.customerAddress = d.customerAddress || '';
      this.form.customerEmail = d.customerEmail || '';
      this.form.chassisNumber = d.chassisNumber || '';
      this.form.carBringer = d.carBringer || '';
      this.form.carBringerPhone = d.carBringerPhone || '';
      this.form.odometer = d.odometer != null ? d.odometer : null;
      this.form.surveyorName = d.surveyorName || '';
      this.form.estimatedDeliveryTime = d.estimatedDeliveryTime
        ? moment(d.estimatedDeliveryTime).format('YYYY-MM-DDTHH:mm')
        : '';
      this.form.usagePurpose = d.usagePurpose || '';
      this.form.customerRequest = d.customerRequest || '';
      this.form.discountType = d.discountType || DISCOUNT_TYPE.AMOUNT;
      this.form.discountAmount = num(d.discountAmount);
      this.form.discountCouponType =
        d.discountCouponType || DISCOUNT_TYPE.AMOUNT;
      this.form.discountCouponAmount = num(d.discountCouponAmount);
      this.form.insurancePayAmount = num(d.insurancePayAmount);
    },
    toRow(it) {
      return {
        productSelected: it.productId
          ? { id: it.productId, name: it.productName, label: it.productName }
          : null,
        productId: it.productId,
        productName: it.productName,
        productGroupId: it.productGroupId || null,
        productGroupName: it.productGroupName || null,
        technicianName: it.technicianName || '',
        unit: it.unit || '',
        unitPrice: num(it.unitPrice),
        quantity: num(it.quantity),
        discountType: it.discountType || DISCOUNT_TYPE.AMOUNT,
        discountAmount: num(it.discountAmount),
        vatType: it.vatType || '0%',
        vatRate: num(it.vatRate),
        notes: it.notes || '',
      };
    },
    async loadCategories() {
      try {
        const res = await CategoryService.getAll({
          getFull: true,
          limit: 1000,
        });
        const body = (res.data && res.data.data) || [];
        const list = Array.isArray(body) ? body : body.data || [];
        // BE trả cây danh mục (cấp 0 + `children` lồng) → giữ nguyên cho treeselect,
        // đồng thời build map id→tên để resolve productGroupName mọi cấp.
        const nameById = {};
        const walk = (nodes) => {
          (nodes || []).forEach((c) => {
            if (!c || !c.id) return;
            nameById[c.id] = c.name;
            if (Array.isArray(c.children) && c.children.length) {
              walk(c.children);
            }
          });
        };
        walk(list);
        this.categoryTree = list;
        this.categoryNameById = nameById;
      } catch (e) {
        this.categoryTree = [];
        this.categoryNameById = {};
      }
    },
    addItem() {
      this.items.push(emptyItem());
    },
    removeItem(idx) {
      this.items.splice(idx, 1);
    },
    normalizerCate(node) {
      return { id: node.id, label: node.name, children: node.children };
    },
    onGroupChange(it, val) {
      it.productGroupId = val || null;
      it.productGroupName = val ? this.categoryNameById[val] || null : null;
    },
    // Toggle loại chiết khấu AMOUNT ↔ PERCENT (tham khảo contracts/ProductTable.vue).
    // Reset số về 0 để tránh nhầm (vd 1.000.000đ thành 1.000.000%).
    toggleDiscType(obj, typeKey, amountKey) {
      obj[typeKey] =
        obj[typeKey] === DISCOUNT_TYPE.PERCENT
          ? DISCOUNT_TYPE.AMOUNT
          : DISCOUNT_TYPE.PERCENT;
      obj[amountKey] = 0;
    },
    discTitle(type) {
      return type === DISCOUNT_TYPE.PERCENT
        ? 'Phần trăm — bấm để chuyển sang Tiền'
        : 'Tiền — bấm để chuyển sang Phần trăm';
    },
    async runProductSearch(query) {
      const text = (query || '').trim();
      if (!text || text.length < 2) {
        this.productOptions = [];
        this.productLoading = false;
        return;
      }
      this.productLoading = true;
      try {
        const { data } = await productService.getProducts({
          page: 1,
          limit: 30,
          search: text,
          // Phiếu dịch vụ chỉ hỗ trợ hàng theo số lượng → loại xe/hàng theo số khung/serial khỏi dropdown.
          excludeTypes: 'CHASSIS_ENGINE,SERIES',
        });
        const rows = get(data, 'data.data') || get(data, 'data') || [];
        this.productOptions = Array.isArray(rows)
          ? rows.map((p) => ({
              id: p.id,
              name: p.name,
              code: p.code,
              label: p.code ? `${p.code} - ${p.name}` : p.name,
              unit: get(p, 'uom.name', ''),
              sellingPrice: num(p.sellingPrice),
              vatType: p.vatType || '0%',
              categoryId: p.categoryId || null,
              categoryName: get(p, 'category.name', null),
            }))
          : [];
      } catch (e) {
        this.productOptions = [];
      } finally {
        this.productLoading = false;
      }
    },
    onProductSelected(it, opt) {
      if (!opt) return;
      it.productId = opt.id;
      it.productName = opt.name;
      it.unit = opt.unit || it.unit;
      it.unitPrice = num(opt.sellingPrice);
      it.vatType = opt.vatType || '0%';
      it.vatRate = vatTypeToRate(it.vatType);
      if (opt.categoryId) {
        it.productGroupId = opt.categoryId;
        it.productGroupName = opt.categoryName;
        // Phòng khi map danh mục chưa có nhóm này → lưu tên để resolve đúng tên nhóm.
        if (opt.categoryName && !this.categoryNameById[opt.categoryId]) {
          this.$set(this.categoryNameById, opt.categoryId, opt.categoryName);
        }
      }
    },
    // @remove (bỏ chọn) → xoá thông tin SP của dòng, ĐỐI XỨNG với onProductSelected.
    // Thiếu hàm này thì multiselect chỉ set productSelected = null (v-model): ô hiện lại placeholder
    // nhưng productId/unitPrice/vatType vẫn giữ giá trị cũ → validateItems lọt (productId còn truthy) và
    // buildPayload gửi lại SP cũ ⇒ nhìn thấy trống mà vẫn lưu SP đã bỏ, đúng giá cũ.
    // Bỏ chọn xong dòng sẽ thiếu SP → validateItems chặn khi Lưu; muốn bỏ hẳn dòng thì dùng nút xoá dòng.
    onProductRemoved(it) {
      this.$set(it, 'productSelected', null);
      this.$set(it, 'productId', null);
      this.$set(it, 'productName', '');
      this.$set(it, 'productGroupId', null);
      this.$set(it, 'productGroupName', null);
      this.$set(it, 'unit', '');
      this.$set(it, 'unitPrice', 0);
      this.$set(it, 'vatType', '0%');
      this.$set(it, 'vatRate', 0);
    },
    buildPayload() {
      return {
        customerAddress: this.form.customerAddress || undefined,
        customerEmail: this.form.customerEmail || undefined,
        chassisNumber: this.form.chassisNumber || undefined,
        carBringer: this.form.carBringer || undefined,
        carBringerPhone: this.form.carBringerPhone || undefined,
        odometer:
          this.form.odometer != null && this.form.odometer !== ''
            ? Number(this.form.odometer)
            : undefined,
        surveyorName: this.form.surveyorName || undefined,
        estimatedDeliveryTime: this.form.estimatedDeliveryTime || undefined,
        usagePurpose: this.form.usagePurpose || undefined,
        customerRequest: this.form.customerRequest || undefined,
        discountType: this.form.discountType,
        discountAmount: num(this.form.discountAmount),
        discountCouponType: this.form.discountCouponType,
        discountCouponAmount: num(this.form.discountCouponAmount),
        insurancePayAmount: num(this.form.insurancePayAmount),
        // KHÔNG gửi customerPayAmount/vatType/vatRate — BE tự tính / lấy từ product.
        items: this.items.map((it) => ({
          productId: it.productId,
          productName: it.productName,
          productGroupId: it.productGroupId || undefined,
          productGroupName: it.productGroupName || undefined,
          technicianName: it.technicianName || undefined,
          unit: it.unit || 'Cái',
          unitPrice: num(it.unitPrice),
          quantity: num(it.quantity),
          discountType: it.discountType,
          discountAmount: num(it.discountAmount),
          notes: it.notes || undefined,
        })),
      };
    },
    validateItems() {
      for (let i = 0; i < this.items.length; i++) {
        const it = this.items[i];
        if (!it.productId) {
          makeToastFaile(`Dòng ${i + 1}: chưa chọn sản phẩm/dịch vụ`);
          return false;
        }
        if (num(it.quantity) <= 0) {
          makeToastFaile(`Dòng ${i + 1}: số lượng phải > 0`);
          return false;
        }
        if (num(it.unitPrice) < 0) {
          makeToastFaile(`Dòng ${i + 1}: đơn giá không hợp lệ`);
          return false;
        }
      }
      return true;
    },
    async onUpdate() {
      if (!this.validateItems()) return;
      this.saving = true;
      try {
        const res = await ServiceOrderService.updateQuote(
          this.id,
          this.buildPayload(),
        );
        const data = (res.data && res.data.data) || {};
        this.order = data;
        this.fillForm(data);
        this.items = (data.items || []).map((it) => this.toRow(it));
        makeToastSuccess('Cập nhật báo giá thành công');
        this.goDetail();
      } catch (e) {
        makeToastFaile(this.errMsg(e, 'Cập nhật báo giá thất bại'));
      } finally {
        this.saving = false;
      }
    },
    goDetail() {
      this.$router.push({
        name: 'detail-service-order',
        params: { id: this.id },
      });
    },
    errMsg(e, fallback) {
      return get(e, 'response.data.message') || fallback;
    },
  },
};
</script>

<style scoped>
.so-card {
  background: #fff;
  border: 1px solid #ebedf3;
  border-radius: 6px;
  padding: 16px;
}
.so-card-title {
  font-weight: 600;
  color: #3f4254;
}
.so-label {
  font-size: 0.85rem;
  color: #7e8299;
  margin-bottom: 2px;
}
.so-ro {
  padding: 6px 12px;
  font-size: 0.9rem;
}
.so-ro span {
  color: #7e8299;
}
.so-quote-status {
  display: inline-flex;
  align-items: center;
  gap: 8px;
}
.so-quote-no {
  font-family: monospace;
  font-weight: 600;
}
/* Sàn rộng → khi viewport hẹp, .table-responsive bật thanh cuộn ngang thay vì bóp
   cột (kéo qua để xem rõ mọi cột). */
.so-item-table {
  min-width: 1480px;
}
.so-item-table th {
  background: #f3f6f9;
  font-size: 12px;
  vertical-align: middle;
}
.so-item-table td {
  vertical-align: middle;
  padding: 4px 6px;
}
/* Treeselect cột Nhóm — thu gọn cho khít hàng (cao bằng input size sm). */
.so-cate-tree ::v-deep .vue-treeselect__control {
  height: 31px;
  border: 1px solid #e4e6ef;
  border-radius: 6px;
}
.so-cate-tree ::v-deep .vue-treeselect__placeholder,
.so-cate-tree ::v-deep .vue-treeselect__single-value {
  line-height: 29px;
  font-size: 13px;
}
.so-cate-tree ::v-deep .vue-treeselect__value-container {
  font-size: 13px;
}
/* Sàn rộng cho cột Tên SP/DV — chống auto-layout bóp về min-content (vỡ chữ dọc). */
.so-col-name {
  min-width: 240px;
}
.so-ms-name {
  font-size: 13px;
  font-weight: 500;
  color: #3f4254;
  white-space: normal;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.so-ms-option {
  display: flex;
  flex-direction: column;
  gap: 1px;
}
.so-ms-code {
  font-size: 11px;
  color: #b5b5c3;
  font-family: monospace;
}
/* Widget chiết khấu (input + nút toggle %/đ) — tham khảo contracts/ProductTable.vue. */
.so-disc {
  width: 100%;
  height: 31px;
}
.so-disc-input {
  flex: 1;
  min-width: 0;
}
/* Ép cùng chiều cao cho CẢ 2 nhánh BInputNumber (input % thường không có size sm,
   mặc định cao ~38px) → toggle không làm đổi kích thước, không nháy layout. */
.so-disc-input ::v-deep .form-control {
  height: 31px;
  min-height: 31px;
  font-size: 13px;
  border-top-right-radius: 0;
  border-bottom-right-radius: 0;
  border-right: 0;
}
.so-disc-toggle {
  width: 34px;
  height: 31px;
  flex-shrink: 0;
  border-top-left-radius: 0 !important;
  border-bottom-left-radius: 0 !important;
  padding: 0;
}
.so-total-row {
  display: flex;
  justify-content: space-between;
  padding: 4px 0;
  font-size: 0.95rem;
}
.so-total-final {
  border-top: 1px solid #ebedf3;
  margin-top: 4px;
  padding-top: 8px;
  font-size: 1.05rem;
  color: #1bc5bd;
}
.so-total-note {
  margin-top: 6px;
  font-size: 0.78rem;
  color: #b5b5c3;
  font-style: italic;
}
</style>

<!-- Override vue-multiselect theo Metronic cho bảng item báo giá — KHÔNG scoped để
     chạm class nội bộ .multiselect__*, prefix .so-quote để không ảnh hưởng nơi khác.
     Tham khảo pattern từ vehicle-purchase-orders/Upsert.vue. -->
<style lang="scss">
.so-quote {
  .multiselect {
    min-height: 31px;
  }
  /* Flex + align-items center → single label (cao 2 dòng tên dài) căn giữa ô,
     chừa 40px phải cho mũi tên. */
  .multiselect__tags {
    display: flex;
    align-items: center;
    flex-wrap: wrap;
    min-height: 31px;
    height: auto;
    padding: 2px 40px 2px 8px;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    font-size: 13px;
  }
  .multiselect__single,
  .multiselect__input {
    font-size: 13px;
    line-height: 1.3;
    margin-bottom: 0;
    padding: 0;
    background: transparent;
    white-space: normal;
  }
  /* KHÔNG ép min-width cho input: vue-multiselect set width=0 khi đã chọn để ẩn
     input; ép rộng sẽ làm placeholder đè lên tên SP đã chọn. */
  .multiselect__input {
    flex: 0 1 auto;
    min-width: 0;
  }
  .multiselect__placeholder {
    font-size: 13px;
    color: #b5b5c3;
    padding: 0;
    margin-bottom: 0;
    line-height: 1.3;
  }
  .multiselect__select {
    height: 29px;
  }
  .multiselect__content-wrapper {
    background: #fff;
    border: 1px solid #e4e6ef;
    border-radius: 6px;
    box-shadow: 0 6px 18px rgba(0, 0, 0, 0.08);
    z-index: 60;
  }
  .multiselect__option {
    min-height: auto;
    padding: 8px 12px;
    font-size: 13px;
  }
  /* Bỏ nền xanh lá mặc định → hover xám nhạt, chữ tối (đồng bộ Metronic). */
  .multiselect__option--highlight {
    background: #f3f6f9;
    color: #3f4254;
  }
  .multiselect__option--highlight::after {
    display: none;
  }
  .multiselect__option--selected {
    background: #ebedf3;
    color: #3f4254;
    font-weight: 500;
  }
  .multiselect__option--selected.multiselect__option--highlight {
    background: #e1e3ea;
    color: #3f4254;
  }
}
</style>
